import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { doc, setDoc, serverTimestamp, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { getTodayId } from "../utils/date";

import { getQuestion, getJourneyConfig, isGateQuestion } from "../lib/questions";
import { saveAnswer, updateProfileField } from "../lib/answers";
import { updatePostnatalPhaseIfPossible } from "../lib/profile";
import type { QuestionDoc, QuestionOption } from "../types/questions";

import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";

import FtuTopNav from "../components/FtuTopNav";
import FtuQuestionCard from "../components/FtuQuestionCard";
import FtuDateInput from "../components/FtuDateInput";
import { IconCheck } from "@tabler/icons-react";

// ---------------------------------------------------------------------------
// Types & Constants
// ---------------------------------------------------------------------------
type Journey = "postnatal" | "antenatal" | "circumstances";

interface JourneyConfig {
  maxSteps: number;
  displayName: string;
  enableRecoveryProgramme?: boolean;
  recoveryProgrammeId?: string;
  recoveryProgrammeWeeks?: number;
}

const FIRST_QUESTION_ID = "FTU.GATE.0";

const PAGE_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  enter: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: "easeOut" as const },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.15, ease: "easeIn" as const },
  },
};

const journeyLabelMap: Record<Journey, string> = {
  postnatal: "Postnatal Follow-ups",
  antenatal: "Antenatal Follow-ups",
  circumstances: "Circumstances Follow-ups",
};

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------
const normalizeJourney = (raw: string): Journey => {
  const v = raw.toLowerCase().trim();
  if (v.includes("post") || v === "postnatal") return "postnatal";
  if (v.includes("ante") || v === "antenatal") return "antenatal";
  return "circumstances";
};

const toLocalDateString = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fromLocalDateString = (value: string): Date =>
  new Date(value.replace(/-/g, "/"));

const parseDateRules = (rules?: string): { minDate?: Date; maxDate?: Date } => {
  if (!rules) return {};

  const result: { minDate?: Date; maxDate?: Date } = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parts = rules.split(",").map((r) => r.trim());

  for (const part of parts) {
    if (part === "max=today") {
      result.maxDate = new Date(today);
    } else if (part === "min=today") {
      result.minDate = new Date(today);
    } else if (part.startsWith("max=today+")) {
      const daysMatch = part.match(/max=today\+(\d+)d/);
      if (daysMatch) {
        const days = parseInt(daysMatch[1], 10);
        const maxDate = new Date(today);
        maxDate.setDate(maxDate.getDate() + days);
        result.maxDate = maxDate;
      }
    } else if (part.startsWith("min=today+")) {
      const daysMatch = part.match(/min=today\+(\d+)d/);
      if (daysMatch) {
        const days = parseInt(daysMatch[1], 10);
        const minDate = new Date(today);
        minDate.setDate(minDate.getDate() + days);
        result.minDate = minDate;
      }
    }
  }

  return result;
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const Ftu: React.FC = () => {
  const { user, setFtuCompleted } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<{ ftuCompleted?: boolean } | null>(null);

  const [currentQuestion, setCurrentQuestion] = useState<QuestionDoc | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [history, setHistory] = useState<string[]>([]);
  const [journey, setJourney] = useState<Journey | null>(null);
  const [journeyConfig, setJourneyConfig] = useState<JourneyConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFinishing, setIsFinishing] = useState(false);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const requestIdRef = useRef(0);

  // ---------------------------------------------------------------------------
  // Initial Load
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        setProfile(snap.exists() ? (snap.data() as { ftuCompleted?: boolean }) : {});
      },
      (err) => {
        console.error("Ftu: could not load profile", err);
        setProfile({});
      }
    );

    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const initialize = async () => {
      try {
        const firstQuestion = await getQuestion(FIRST_QUESTION_ID);
        setCurrentQuestion(firstQuestion);
        setIsLoading(false);

        setDoc(
          doc(db, "users", user.uid),
          { _rules_probe: Date.now() },
          { merge: true }
        ).catch(() => {});

        setDoc(
          doc(db, "questionnaires", user.uid, "responses", "FTU.RULES.PROBE"),
          { ok: true, ts: Date.now() },
          { merge: true }
        ).catch(() => {});
      } catch (err) {
        console.error("Error initializing FTU:", err);
        setIsLoading(false);
      }
    };

    initialize();
  }, [user]);

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------
  const categoryLabel = useMemo(() => {
    if (
      !currentQuestion ||
      currentQuestion.type === "info" ||
      isGateQuestion(currentQuestion.id)
    ) {
      return undefined;
    }
    return journey ? journeyLabelMap[journey] : undefined;
  }, [currentQuestion, journey]);

  const progressConfig = useMemo(() => {
    if (!journeyConfig || !currentQuestion) {
      return { show: false, current: 0, max: 0 };
    }

    const max = journeyConfig.maxSteps;
    const historyCount = history.filter((id) => !isGateQuestion(id)).length;

    const current = isGateQuestion(currentQuestion.id)
      ? historyCount
      : historyCount + 1;

    return {
      show: !isGateQuestion(currentQuestion.id),
      current: Math.min(current, max),
      max,
    };
  }, [journeyConfig, currentQuestion, history]);

  const continueButtonState = useMemo(() => {
    if (!currentQuestion) return { disabled: true, text: "Continue" };

    const q = currentQuestion;
    const answer = answers[q.id];

    if (q.type === "info") {
      return { disabled: false, text: q.next ? "Continue" : "Finish" };
    }

    const isRequired = q.required !== false;

    let disabled = false;
    if (isRequired) {
      if (q.type === "multiple") {
        disabled = !Array.isArray(answer) || answer.length === 0;
      } else if (q.type === "text") {
        disabled =
          !answer ||
          (typeof answer === "string" && answer.trim().length === 0);
      } else {
        disabled = !answer;
      }
    }

    const text =
      q.next || q.options?.some((o) => o.next) ? "Continue" : "Finish";

    return { disabled, text };
  }, [currentQuestion, answers]);

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------
  const loadQuestion = useCallback(async (questionId: string) => {
    const myReqId = ++requestIdRef.current;
    setIsTransitioning(true);

    try {
      const q = await getQuestion(questionId);
      if (requestIdRef.current === myReqId) {
        setCurrentQuestion(q);
      }
    } catch (err) {
      console.error("Error loading question:", questionId, err);
    } finally {
      if (requestIdRef.current === myReqId) {
        setIsTransitioning(false);
      }
    }
  }, []);

  const completeFtu = useCallback(async () => {
    if (!user) return;
    setIsFinishing(true);

    try {
      await updateProfileField(user.uid, "ftuCompleted", true);
      setFtuCompleted(true);
      await updatePostnatalPhaseIfPossible(user.uid);

      if (
        journeyConfig?.enableRecoveryProgramme &&
        journeyConfig.recoveryProgrammeId
      ) {
        const programmeRef = doc(
          db,
          "users",
          user.uid,
          "recoveryProgrammes",
          journeyConfig.recoveryProgrammeId
        );

        const progSnap = await getDoc(programmeRef);
        if (!progSnap.exists()) {
          await setDoc(programmeRef, {
            programmeId: journeyConfig.recoveryProgrammeId,
            startDateId: getTodayId(),
            totalWeeks: journeyConfig.recoveryProgrammeWeeks || 6,
            status: "active",
            confirmedWeek: 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      }

      confetti({ particleCount: 120, spread: 70, origin: { y: 0.3 } });
      setTimeout(() => navigate("/dashboard"), 150);
    } catch (err) {
      console.error("Error completing FTU:", err);
      setTimeout(() => navigate("/dashboard"), 150);
    }
  }, [user, journeyConfig, navigate, setFtuCompleted]);

  const executeTask = useCallback(
    async (taskId: string) => {
      if (!user) return;

      if (taskId === "questionnaire.complete") {
        await completeFtu();
        return;
      }

      if (taskId.startsWith("SET_SUPPORT_MODE:")) {
        const mode = taskId.split(":")[1];
        updateProfileField(user.uid, "supportMode", mode).catch(console.error);
      }
    },
    [user, completeFtu]
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleNext = useCallback(async () => {
    if (!currentQuestion || !user) return;
    if (isTransitioning) return;

    const q = currentQuestion;

    if (q.type === "info") {
      if (q.taskOnComplete) {
        await executeTask(q.taskOnComplete);
        return;
      }

      if (q.next) {
        setHistory((prev) => [...prev, q.id]);
        await loadQuestion(String(q.next));
      } else {
        await completeFtu();
      }
      return;
    }

    const answerValue = answers[q.id];

    if (q.required) {
      if (q.type === "multiple") {
        if (!Array.isArray(answerValue) || answerValue.length === 0) return;
      } else if (!answerValue) return;
    }

    saveAnswer(user.uid, q.id, { answer: answerValue }).catch(console.error);

    let nextId: string | null = null;

    if (q.isFirst) {
      const norm = normalizeJourney(String(answerValue));
      const config = await getJourneyConfig(norm);

      setJourney(norm);
      setJourneyConfig(config);

      updateProfileField(user.uid, "ftuPath", norm).catch(console.error);

      const selectedOption = q.options?.find(
        (opt) =>
          String(opt.id) === String(answerValue) ||
          String(opt.value) === String(answerValue)
      );

      if (selectedOption?.next) nextId = String(selectedOption.next);
    } else {
      let selectedOption: QuestionOption | undefined;

      if (q.type === "single" && q.options) {
        selectedOption = q.options.find((opt) => {
          const optVal = String(opt.value ?? opt.id);
          return optVal === String(answerValue);
        });
      }

      if (q.type === "multiple" && q.options) {
        const arr = Array.isArray(answerValue) ? answerValue : [];
        selectedOption = q.options.find((opt) => {
          const optVal = String(opt.value ?? opt.id);
          return !!opt.next && arr.includes(optVal);
        });
      }

      if (selectedOption?.taskOnSelect) {
        await executeTask(selectedOption.taskOnSelect);
      }

      nextId = selectedOption?.next
        ? String(selectedOption.next)
        : q.next
        ? String(q.next)
        : null;
    }

    if (q.taskOnComplete) {
      await executeTask(q.taskOnComplete);
      if (q.taskOnComplete === "questionnaire.complete") return;
    }

    if (nextId) {
      setHistory((prev) => [...prev, q.id]);
      await loadQuestion(nextId);
    } else {
      await completeFtu();
    }
  }, [
    currentQuestion,
    user,
    answers,
    isTransitioning,
    executeTask,
    loadQuestion,
    completeFtu,
  ]);

  const handleBack = useCallback(async () => {
    if (isTransitioning) return;

    let targetId = FIRST_QUESTION_ID;

    setHistory((prev) => {
      if (prev.length === 0) return prev;
      targetId = prev[prev.length - 1];
      return prev.slice(0, -1);
    });

    await loadQuestion(targetId);
  }, [isTransitioning, loadQuestion]);

  const handleAnswerChange = useCallback(
    (questionId: string, value: unknown) => {
      setAnswers((prev) => ({ ...prev, [questionId]: value }));
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Render Question
  // ---------------------------------------------------------------------------
  const renderQuestion = useCallback(() => {
    if (!currentQuestion) return null;

    const q = currentQuestion;
    const answer = answers[q.id];
    const opts = q.options || [];
    const getLabel = (opt: QuestionOption) =>
      String(opt.text ?? opt.label ?? opt.name ?? opt.value ?? opt.id ?? "");

    switch (q.type) {
      case "single": {
        // ── Special UI for the first gate question only ──
        if (q.isFirst) {
          const mainOptions = opts.slice(0, 2);
          const altOption = opts[2];

          return (
            <div className="flex flex-col gap-3 w-full">
              {/* Main options — card style with icon + subtitle + chevron */}
              {mainOptions.map((opt, index) => {
                const val = String(opt.value ?? opt.id);
                const isSelected = answer === val;

                const icon =
                  index === 0 ? (
                    // Heart icon — postnatal — mint circle background
                    <div className="w-10 h-10 rounded-full bg-[#e0f2ec] flex items-center justify-center shrink-0">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 21C12 21 3 14.5 3 8.5C3 5.42 5.42 3 8.5 3C10.07 3 11.54 3.68 12 4.5C12.46 3.68 13.93 3 15.5 3C18.58 3 21 5.42 21 8.5C21 14.5 12 21 12 21Z"
                          stroke="#1e5e5b"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </svg>
                    </div>
                  ) : (
                    // Calendar icon — antenatal — light blue circle background
                    <div className="w-10 h-10 rounded-full bg-[#dbeafe] flex items-center justify-center shrink-0">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="4" width="18" height="17" rx="2" fill="#3b82f6" />
                        <path d="M3 9h18" stroke="white" strokeWidth="1.5" />
                        <path d="M8 2v4M16 2v4" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" />
                        <rect x="3" y="2.5" width="18" height="6" rx="2" fill="#3b82f6" />
                        <path d="M8 2v5M16 2v5" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
                        <text x="12" y="19" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold" fontFamily="DM Sans, sans-serif">1</text>
                      </svg>
                    </div>
                  );

                const subtitle =
                  index === 0
                    ? "Start your postnatal recovery"
                    : "Prepare for birth and beyond";

                return (
                  <button
                    key={opt.id ?? val}
                    type="button"
                    onClick={() => handleAnswerChange(q.id, val)}
                    className={`w-full px-4 py-3 rounded-2xl text-left transition-all flex items-center gap-3 ${
                      isSelected
                        ? "bg-white ring-2 ring-[#1e5e5b]"
                        : "bg-white hover:bg-gray-50"
                    }`}
                    style={{ fontFamily: "DM Sans, sans-serif" }}
                  >
                    {icon}
                    <div className="flex flex-col flex-1">
                      <span className="font-semibold text-base text-[#0c2624]">
                        {getLabel(opt)}
                      </span>
                      <span className="text-xs text-[#757575] mt-0.5">
                        {subtitle}
                      </span>
                    </div>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="shrink-0 text-[#b3b3b3]"
                    >
                      <path
                        d="M9 18l6-6-6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                );
              })}

              {/* "or" divider */}
              {altOption && (
                <>
                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span
                      className="text-xs text-[#b3b3b3]"
                      style={{ fontFamily: "DM Sans, sans-serif" }}
                    >
                      or
                    </span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Alt option — lighter style */}
                  <button
                    type="button"
                    onClick={() =>
                      handleAnswerChange(
                        q.id,
                        String(altOption.value ?? altOption.id)
                      )
                    }
                    className={`w-full px-4 py-3 rounded-2xl text-left transition-all flex items-center gap-3 ${
                      answer === String(altOption.value ?? altOption.id)
                        ? "bg-white ring-2 ring-[#1e5e5b]"
                        : "hover:bg-gray-50"
                    }`}
                    style={{ fontFamily: "DM Sans, sans-serif" }}
                  >
                    {/* Cleaned Split Arrow Icon */}
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#4a4a4a" 
                      strokeWidth="2"
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      id="Arrows-Split-2" 
                      height="24" 
                      width="24"
                    >
                      <path d="M21 17h-5.397a5 5 0 0 1 -4.096 -2.133l-0.514 -0.734A5 5 0 0 0 6.897 12H3" />
                      <path d="M21 7h-5.395a5 5 0 0 0 -4.098 2.135l-0.51 0.73A5 5 0 0 1 6.9 12H3" />
                      <path d="m18 10 3 -3 -3 -3" />
                      <path d="m18 20 3 -3 -3 -3" />
                    </svg>

                    <span className="flex-1 font-medium text-base text-[#4a4a4a]">
                      {getLabel(altOption)}
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="shrink-0 text-[#b3b3b3]"
                    >
                      <path
                        d="M9 18l6-6-6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </>
              )}
            </div>
          );
        }

        // ── Default single question UI (all other questions unchanged) ──
        return (
          <div className="flex flex-col gap-6 w-full">
            {opts.map((opt) => {
              const val = String(opt.value ?? opt.id);
              const isSelected = answer === val;
              return (
                <button
                  key={opt.id ?? val}
                  type="button"
                  onClick={() => handleAnswerChange(q.id, val)}
                  className={`w-full min-h-[56px] px-4 py-3 rounded-2xl text-left transition-all font-semibold text-xl text-[#0c2624] ${
                    isSelected
                      ? "bg-white ring-2 ring-[#1e5e5b]"
                      : "bg-white hover:bg-gray-50"
                  }`}
                  style={{ fontFamily: "DM Sans, sans-serif" }}
                >
                  {getLabel(opt)}
                </button>
              );
            })}
          </div>
        );
      }

      case "multiple":
        return (
          <div className="flex flex-wrap gap-3 w-full">
            {opts.map((opt) => {
              const val = String(opt.value ?? opt.id);
              const arr = Array.isArray(answer) ? answer : [];
              const isSelected = arr.includes(val);
              return (
                <button
                  key={opt.id ?? val}
                  type="button"
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-2xl transition-all text-sm font-normal text-black ${
                    isSelected
                      ? "bg-[#e3ebd5] border border-[#1e5e5b]"
                      : "bg-[#e3ebd5] border border-transparent"
                  }`}
                  style={{ fontFamily: "DM Sans, sans-serif" }}
                  onClick={() => {
                    const next = new Set(arr);
                    if (isSelected) next.delete(val);
                    else next.add(val);
                    handleAnswerChange(q.id, Array.from(next));
                  }}
                >
                  <span>{getLabel(opt)}</span>
                  {isSelected && (
                    <IconCheck
                      size={20}
                      strokeWidth={2}
                      className="text-[#1e5e5b]"
                    />
                  )}
                </button>
              );
            })}
          </div>
        );

      case "date": {
        const { minDate, maxDate } = parseDateRules(q.rules);
        return (
          <FtuDateInput
            value={
              answer && typeof answer === "string"
                ? fromLocalDateString(answer)
                : null
            }
            onChange={(date: Date | null) => {
              if (!date) {
                handleAnswerChange(q.id, null);
                return;
              }
              handleAnswerChange(q.id, toLocalDateString(date));
            }}
            placeholder={q.placeholder || "dd/mm/yyyy"}
            minDate={minDate}
            maxDate={maxDate}
          />
        );
      }

      case "text":
        return (
          <textarea
            className="w-full min-h-[137px] px-4 py-3 bg-white rounded-lg text-base text-[#1e1e1e] placeholder:text-[#b3b3b3] resize-none focus:outline-none focus:ring-2 focus:ring-[#1e5e5b]"
            value={typeof answer === "string" ? answer : ""}
            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
            rows={4}
            placeholder={q.placeholder || ""}
            style={{ fontFamily: "DM Sans, sans-serif" }}
          />
        );

      case "info":
        return (
          <p
            className="text-lg text-[#757575]"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {q.text}
          </p>
        );

      default:
        return (
          <div className="text-red-500">
            Unsupported question type: {String(q.type)}
          </div>
        );
    }
  }, [currentQuestion, answers, handleAnswerChange]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!user) return <Navigate to="/login" />;
  if (user && sessionStorage.getItem(`bodyown_account_deleted_${user.uid}`)) {
    return <Navigate to="/login" replace />;
  }
  if (profile?.ftuCompleted) return <Navigate to="/dashboard" replace />;

  if (isLoading || profile === null || isFinishing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100%',
        minHeight: '-webkit-fill-available',
        backgroundColor: '#fcf9f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
        pointerEvents: 'all'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            {isFinishing ? "Preparing your journey" : "BodyOwn"}
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          body { overflow: hidden !important; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#fcf9f7] flex flex-col">
      {currentQuestion && (
        <FtuTopNav
          canGoBack={history.length > 0 && !isTransitioning}
          onBack={handleBack}
          onExit={() => navigate("/dashboard")}
          showProgress={progressConfig.show}
          currentStep={progressConfig.current}
          maxSteps={progressConfig.max}
        />
      )}

      <div className="w-full max-w-[680px] mx-auto px-6 sm:px-6">
        <AnimatePresence mode="wait">
          {currentQuestion && (
            <motion.div
              key={currentQuestion.id}
              variants={PAGE_VARIANTS}
              initial="initial"
              animate="enter"
              exit="exit"
            >
              <FtuQuestionCard
                category={categoryLabel}
                question={currentQuestion.text}
                helpText={
                  currentQuestion.type !== "info"
                    ? currentQuestion.helpText
                    : undefined
                }
                isInfoScreen={currentQuestion.type === "info"}
              >
                {renderQuestion()}
              </FtuQuestionCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {currentQuestion && (
        <div className="w-full max-w-[680px] mx-auto px-6 sm:px-6 mt-6 pb-6">
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleNext}
            disabled={continueButtonState.disabled || isTransitioning}
            className={`w-full h-12 px-4 rounded-full font-semibold text-base transition-all ${
              continueButtonState.disabled || isTransitioning
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#1e5e5b] text-white hover:opacity-90"
            }`}
          >
            {isTransitioning ? "Loading..." : continueButtonState.text}
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default Ftu;